<script lang="ts" setup>
import { useToast } from 'primevue/usetoast'

definePageMeta({
	auth: false,
	layout: false
})

const { signIn } = useAuth()
const popup = useToast()
const loading = ref(false)

const infoApp = appConfig.infoapp
const username = ref('')
const password = ref('')
const checked = ref(false)

// Validation refs
const emailInvalid = ref(false)
const passwordInvalid = ref(false)

// Validation methods
const validateEmail = () => {
	emailInvalid.value = username.value.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username.value)
}

const validatePassword = () => {
	passwordInvalid.value = password.value.length > 0 && password.value.length < 6
}

// Form validation computed
const isFormValid = computed(() =>
	username.value.length > 0 &&
	password.value.length >= 6 &&
	!emailInvalid.value &&
	!passwordInvalid.value
)

// Load "Remember me" from localStorage on mount
onMounted(() => {
	const savedEmail = localStorage.getItem('savedEmail')
	const savedRemember = localStorage.getItem('rememberMe')
	if (savedRemember === 'true' && savedEmail) {
		username.value = savedEmail
		checked.value = true
	}
})
const router = useRouter()
const currentRoute = useRoute()

const clickLogin = async () => {
	if (username.value === '' || password.value === '') {
		popup.add({
			severity: 'error',
			summary: 'Errore',
			detail: 'Email o password mancanti',
			life: 3000
		})
		return
	}

	loading.value = true

	// Save/clear "Remember me" preference
	if (checked.value) {
		localStorage.setItem('rememberMe', 'true')
		localStorage.setItem('savedEmail', username.value)
	} else {
		localStorage.removeItem('rememberMe')
		localStorage.removeItem('savedEmail')
	}

	const sigin = await signIn('credentials', {
		email: username.value,
		password: password.value,
		redirect: false
	})

	console.log('sigin:', sigin)
	if (sigin && sigin.error) {
		popup.add({
			severity: 'error',
			summary: 'Problema di login',
			detail: sigin.error === 'CredentialsSignin' ? 'Email o password errati' : sigin.error
		})
		password.value = ''
		passwordInvalid.value = false
		loading.value = false
	} else {
		popup.add({
			severity: 'success',
			summary: 'Accesso riuscito',
			detail: 'Login effettuato con successo',
			life: 3000
		})
		loading.value = false

		// Handle callback URL for popup bridge compatibility
		const q = currentRoute.query
		const cb = typeof q.callbackUrl === 'string' ? q.callbackUrl : Array.isArray(q.callbackUrl) ? q.callbackUrl[0] : ''
		if (cb) {
			window.location.href = cb
			return
		}
		await navigateTo('/', { external: true })
	}
}

</script>

<template>
  <div class="login-page-wrapper">
    <Toast />
    <Link rel="stylesheet"
      href="https://cdn.jsdelivr.net/npm/primevue@latest/resources/themes/saga-green/theme.min.css" />

    <!-- Skeleton Overlay -->
    <div v-if="loading" class="login-skeleton-overlay">
      <div class="skeleton-card">
        <div style="text-align: center; margin-bottom: 2rem;">
          <Skeleton width="10rem" height="5rem" border-radius="1rem" class="mb-3" style="margin: 0 auto;" />
        </div>

        <h2 style="font-size: 1.75rem; font-weight: 700; color: #114449; text-align: center; margin: 0 0 0.5rem 0;">
          {{ infoApp.Title }}
        </h2>
        <p style="font-size: 0.9375rem; color: #6b7280; text-align: center; margin: 0 0 2.5rem 0;">
          Caricamento in corso...
        </p>

        <div style="display: flex; flex-direction: column; gap: 1.5rem;">
          <Skeleton width="100%" height="3rem" class="mb-3" />
          <Skeleton width="100%" height="3rem" class="mb-3" />
          <Skeleton width="100%" height="3rem" />
        </div>

        <div style="text-align: center; margin-top: 2rem;">
          <ProgressSpinner stroke-width="4" style="width: 50px; height: 50px; margin: 0 auto 1rem;" />
          <p style="font-size: 0.9375rem; color: #6b7280;">Attendere...</p>
        </div>
      </div>
    </div>

    <!-- Login Card -->
    <div v-else class="login-card">
      <!-- Logo Section -->
      <div class="login-logo">
        <img src="/images/login_icone/Logo_Redigo_LOGIN.png" alt="Redigo Logo" style="max-height: 60px; width: auto;">
      </div>

      <!-- Title Section with secondary logo -->
      <div class="login-title-wrapper">
        <h1 class="login-title">{{ infoApp.Title }}</h1>
        <img src="/images/login_icone/Coccarda_Redigo_LOGIN.png" alt="Coccarda" class="login-badge">
      </div>
      <p class="login-subtitle">Accedi al tuo account</p>

      <!-- Login Form -->
      <div class="login-form">
        <div class="form-field">
          <InputText id="username" v-model="username" type="email" placeholder="nome@esempio.it" autocomplete="email"
            @blur="validateEmail" class="w-full" :class="{ 'p-invalid': emailInvalid }" style="padding: 1rem;" />
          <small v-if="emailInvalid" class="p-error" style="display: block; margin-top: 0.25rem;">
            Email non valida
          </small>
        </div>

        <div class="form-field">
          <Password id="password" v-model="password" toggleMask :feedback="false" placeholder="Inserisci password"
            autocomplete="current-password" @blur="validatePassword" class="w-full"
            :class="{ 'p-invalid': passwordInvalid }"
            input-class="w-full"
            :pt="{
              input: { style: 'padding: 1rem; padding-right: 3.5rem;' },
              showIcon: { style: 'right: 1rem;' },
              hideIcon: { style: 'right: 1rem;' }
            }" />
          <small v-if="passwordInvalid" class="p-error" style="display: block; margin-top: 0.25rem;">
            Password richiesta (minimo 6 caratteri)
          </small>
        </div>

        <div class="flex align-items-center justify-content-between" style="margin-top: 1rem;">
          <div class="flex align-items-center gap-2">
            <Checkbox id="rememberme" v-model="checked" :binary="true" />
            <label for="rememberme" style="font-size: 0.9375rem; color: #6b7280; cursor: pointer;">
              Ricordami
            </label>
          </div>
          <!-- <a href="#" style="font-size: 0.875rem; color: #114449; text-decoration: none;">Password dimenticata?</a> -->
        </div>

        <Button label="Accedi" class="w-full login-button" :disabled="!isFormValid" :loading="loading"
          @click="clickLogin" style="padding: 1rem; font-size: 1rem; font-weight: 600;" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Keyframe for card entrance animation
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Main wrapper with gradient background
.login-page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  background:
    linear-gradient(135deg,
      rgba(17, 68, 73, 0.08) 0%,
      rgba(61, 96, 99, 0.12) 25%,
      rgba(232, 244, 245, 0.95) 50%,
      rgba(212, 233, 235, 0.90) 75%,
      rgba(17, 68, 73, 0.06) 100%
    ),
    radial-gradient(ellipse at top left, rgba(61, 96, 99, 0.15), transparent 50%),
    radial-gradient(ellipse at bottom right, rgba(17, 68, 73, 0.12), transparent 50%),
    linear-gradient(to bottom, #e0eff0, #d4e9eb);
}

// Skeleton overlay (fullscreen)
.login-skeleton-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: white;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skeleton-card {
  max-width: 450px;
  width: 100%;
  padding: 3rem 2.5rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

// Main login card
.login-card {
  max-width: 450px;
  width: 100%;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 3rem 2.5rem;
  animation: slideUp 0.2s ease-out;
}

// Logo section
.login-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

// Title wrapper with badge
.login-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

// Title styles
.login-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #114449;
  text-align: center;
  margin: 0;
  line-height: 1.2;
}

.login-badge {
  max-height: 50px;
  width: auto;
}

.login-subtitle {
  font-size: 0.9375rem;
  color: #52868a;
  text-align: center;
  margin: 0 0 2.5rem 0;
}

// Form container
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
}

// Login button with v4 gradient
.login-button {
  background: linear-gradient(135deg, #3d6063, #114449) !important;
  border: none !important;
  color: white !important;
  font-weight: 600;
  transition: all 0.2s ease;
  pointer-events: auto;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(17, 68, 73, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.p-button-loading {
    pointer-events: none;
  }
}

// Input styling with primary color
:deep(.p-inputtext),
:deep(.p-password input) {
  border-color: #d1d5db;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #3d6063;
  }

  &:focus {
    border-color: #114449;
    box-shadow: 0 0 0 0.2rem rgba(17, 68, 73, 0.15);
  }
}

// Password component wrapper
:deep(.p-password) {
  width: 100%;
}

// Password toggle button
:deep(.p-password-toggle-icon) {
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #114449;
  }
}// Accessibility focus states (WCAG AA compliant)
:deep(.p-inputtext:focus-visible),
:deep(.p-password input:focus-visible),
:deep(.p-checkbox:focus-visible) {
  outline: 2px solid #114449;
  outline-offset: 2px;
}

// Checkbox accent color
:deep(.p-checkbox .p-checkbox-box.p-highlight) {
  background: #114449;
  border-color: #114449;
}

// Responsive breakpoints
@media (max-width: 768px) {
  .login-page-wrapper {
    padding: 1rem;
  }

  .login-card,
  .skeleton-card {
    padding: 2rem 1.5rem;
  }

  .login-title-wrapper {
    flex-direction: column;
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .login-card,
  .skeleton-card {
    padding: 1.5rem 1.25rem;
  }

  .login-title {
    font-size: 1.5rem !important;
  }

  .login-badge {
    max-height: 40px;
  }
}
</style>
